import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HeaderCard from '../../../components/Header/HeaderCard';
import { canGererTypesAbsence } from '../../../components/Auth/permissions';
import TableList from '../../../components/Tools/TableList';
import { apiService } from '../../../components/Config/api.service';
import AbsenceForm from './AbsenceForm';
import AbsenceType from './AbsenceType';
import Drawer from '../../../components/Tools/DrawerCustom';
const Absence = () => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isAbsenceTypeDrawerOpen, setIsAbsenceTypeDrawerOpen] = useState(false);
    // const [drawerAnimating, setDrawerAnimating] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBy, setFilterBy] = useState({});
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
            offset: 0,
            limit: 25,
            total_pages: 1,
            total_count: 0
        });
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const handleRowClick = (id) => {
        setSelectedId(id);
        openDrawer();
    };

    const openDrawer = () => {
        // setDrawerAnimating(true);
        setIsDrawerOpen(true);
    };

    // const closeDrawer = () => {
    //     setDrawerAnimating(true);
    //     setIsDrawerOpen(false);
    //     setTimeout(() => {
    //         setDrawerAnimating(false);
    //     }, 300);
    // };

    

    const formatForfaitLabel = (row) => {
        const [month, year] = (row.mois_annee || '').split('/');
        const monthDate = month && year ? new Date(Number(year), Number(month) - 1, 1) : null;
        const monthLabel = monthDate
            ? monthDate.toLocaleDateString(t('absence.locale') || 'fr-FR', { month: 'long', year: 'numeric' })
            : row.mois_annee;
        return t('absence.forfaitSummary', { count: row.nombre_jours, month: monthLabel });
    };

    const columns = [
        { key: 'employee_name', label: t('payslipsList.employee') },
        {
            key: 'date',
            label: t('absence.startDate'),
            render: (row) => row.saisie_forfait
                ? <span className="whitespace-nowrap">{formatForfaitLabel(row)}</span>
                : row.date
        },
        {
            key: 'date_end',
            label: t('absence.endDate'),
            render: (row) => row.saisie_forfait ? '' : row.date_end
        },
        {
            key: 'absenceTypeCategory',
            label: t('absence.type'),
            render: (value) => {
                let badgeClass = '';
                let badgeLabel = '';

                switch (value.absenceTypeCategory) {
                    case 'deducted':
                        // Utilisation d'une teinte orange douce cohérente avec la palette
                        badgeClass = 'bg-orange-100 text-orange-800 border border-orange-200';
                        badgeLabel = t('absence.deducted');
                        break;
                    case 'paid':
                        // Utilisation d'une teinte verte douce et élégante
                        badgeClass = 'bg-emerald-100 text-emerald-800 border border-emerald-200';
                        badgeLabel = t('absence.paid');
                        break;
                    default:
                        // Utilisation d'une teinte de la couleur primaire pour la cohérence
                        badgeClass = 'bg-slate-100 text-slate-700 border border-slate-200';
                        badgeLabel = t('absence.unpaid');
                        break;
                }

                return (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
                        {badgeLabel}
                    </span>
                );
            }
        },
    ];

    const normalizeFilterBy = (filter = {}) => ({
        ...filter,
        filters: Array.isArray(filter?.filters) ? filter.filters : [],
    });

    const hasActiveFilters = (filter = {}) => normalizeFilterBy(filter).filters.length > 0;

    const getQuickFilterValue = (field) => {
        const currentFilter = normalizeFilterBy(filterBy).filters.find(
            item => item.field === field && item.operator === '='
        );
        return currentFilter ? String(currentFilter.value || '') : '';
    };

    const handleQuickFilterChange = (field, value) => {
        const normalized = normalizeFilterBy(filterBy);
        const filters = normalized.filters.filter(item => item.field !== field);

        if (value) {
            filters.push({
                field,
                operator: '=',
                value,
            });
        }

        const nextFilter = {
            ...normalized,
            filters,
        };

        setFilterBy(nextFilter);
        handlePageChange(1, searchTerm, nextFilter);
    };

    const absenceToolbarFilters = (
        <>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-600 whitespace-nowrap">{t('absence.startDate')}</span>
                <input
                    type="date"
                    className="input input-bordered input-sm bg-white"
                    value={getQuickFilterValue('date')}
                    onChange={(e) => handleQuickFilterChange('date', e.target.value)}
                    aria-label={t('absence.filterByStartDate')}
                />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-600 whitespace-nowrap">{t('absence.endDate')}</span>
                <input
                    type="date"
                    className="input input-bordered input-sm bg-white"
                    value={getQuickFilterValue('date_end')}
                    onChange={(e) => handleQuickFilterChange('date_end', e.target.value)}
                    aria-label={t('absence.filterByEndDate')}
                />
            </div>

            <select
                className="select select-bordered select-sm min-w-[180px] bg-white"
                value={getQuickFilterValue('absenceTypeCategory')}
                onChange={(e) => handleQuickFilterChange('absenceTypeCategory', e.target.value)}
                aria-label={t('absence.filterByType')}
            >
                <option value="">{t('absence.typeAll')}</option>
                {[
                    { value: 'paid', label: t('absence.paid') },
                    { value: 'unpaid', label: t('absence.unpaid') },
                    { value: 'deducted', label: t('absence.deducted') },
                ].map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </>
    );

    const getAbsences = (page = 1, search = '', filter = '') => {
        setLoading(true);
        const offset = (page - 1) * pagination.limit;
        const params = new URLSearchParams();
        params.append('offset', offset);
        params.append('limit', pagination.limit);
        if (search) {
            params.append('search', search);
        }
        if (hasActiveFilters(filter)) {
            params.append('filter_by', JSON.stringify(filter));
        }
        apiService.getElement(`/absences?${params.toString()}`)
            .then(response => {
                setData(response.data.absences);
                const responseData = response.data;
                if (responseData.pagination) {
                    const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;

                    setPagination({
                        ...responseData.pagination,
                        page: currentPage
                    });
                }
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setData([]);
                setPagination({
                    offset: 0,
                    limit: 4,
                    total_pages: 1,
                    total_count: 0
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        getAbsences(pagination.page, searchTerm, filterBy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;
        
        setSearchTerm(newSearchTerm);
        setFilterBy(newFilterBy);
        
        getAbsences(page, newSearchTerm, newFilterBy);
    };

    const refreshAbsenceList = () => getAbsences();

    return (
        <div className="p-6">
            <div className='flex justify-between items-center mb-6'>
                <HeaderCard
                    title={t('leave.absencesAndLeave')}
                    exportProps={{
                        model_name: "absence",
                        fields: ["employee_id", "absence_type_id", "date", "date_end", "hours", "description", "saisie_forfait", "mois_annee", "jours_forfait"],
                        filename: "absences_export",
                        importUrl: "/import/absence",
                        onImportSuccess: refreshAbsenceList
                    }} 
                />

                <div className="flex justify-between items-center gap-2">
                    {/* Yros/Kavero#417 : un type d'absence créé ici devient
                        demandable par les salariés de la société. */}
                    {canGererTypesAbsence() && (
                        <button
                            data-testid="absence-gerer-types"
                            className="btn btn-md bg-gray-500 text-white rounded-full"
                            onClick={() => setIsAbsenceTypeDrawerOpen(true)}
                        >
                            {t('absence.manageAbsenceTypes')}
                        </button>
                    )}
                    <button
                        className="btn btn-md bg-[--color-primary] text-white rounded-full mr-2"
                        onClick={() => { setSelectedId(null); openDrawer(); }}
                    >
                        {t('absence.addAbsence')}
                    </button>
                    
                </div>
            </div>

            <div className="overflow-x-auto card bg-white shadow-sm rounded-lg p-2">
                <TableList
                    columns={columns}
                    data={data}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    searchQuery={searchTerm}
                    setSearchQuery={setSearchTerm}
                    filterBy={filterBy}
                    setFilterBy={setFilterBy}
                    toolbarActions={absenceToolbarFilters}
                    showFilter={false}
                    model={'absence'}
                    onRowClick={row => handleRowClick(row.id)} 
                    loading={loading}
                />
            </div>

            <div className={`fixed inset-0 z-50 ${isDrawerOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                <Drawer isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen} width={600}>
                    <AbsenceForm
                        setIsDrawerOpen={setIsDrawerOpen}
                        id={selectedId}
                        refreshAbsenceList={refreshAbsenceList}
                        refreshTrigger={refreshTrigger}
                    />
                </Drawer>
            </div>
            <div className={`fixed inset-0 z-50 ${isAbsenceTypeDrawerOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                <Drawer isOpen={isAbsenceTypeDrawerOpen} setIsOpen={setIsAbsenceTypeDrawerOpen}>
                    {isAbsenceTypeDrawerOpen && (
                        <AbsenceType
                            setIsDrawerOpen={setIsAbsenceTypeDrawerOpen}
                            // Le sélecteur de `AbsenceForm` relit ses types.
                            refreshParent={() => setRefreshTrigger(prev => prev + 1)}
                        />
                    )}
                </Drawer>
            </div>
        </div>
    );
};

export default Absence;
